// Handler factory for vendor company lists (lenders, titleCompanies, realtors) —
// add, update, delete company entries, and migrate legacy inline companies to the list.
// For TAWs, writes target the parent TW; see src/hooks/vendorTargetWorkspace.js.

import React from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { setDoc } from '../services/firestoreWrites';
import { db } from '../firebase';
import { resolveVendorTargetWorkspaceId } from './vendorTargetWorkspace';

const companyListDocRef = (workspaceId, vendorType) =>
    doc(db, 'workspaces', workspaceId, 'companyLists', vendorType);

const generateCompanyId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const readCurrentCompanies = async (workspaceId, vendorType) => {
    const snap = await getDoc(companyListDocRef(workspaceId, vendorType));
    return snap.exists() && Array.isArray(snap.data().companies) ? snap.data().companies : [];
};

export function useCompanyListHandlers({ selectedWorkspace, run, showToast }) {
    const targetWorkspaceId = resolveVendorTargetWorkspaceId(selectedWorkspace);
    const isTaw = selectedWorkspace?.teamRole === 'team_agent';
    const tawMissingParent = isTaw && !targetWorkspaceId;

    const addCompany = React.useCallback((vendorType, name) =>
        run(async () => {
            if (tawMissingParent) {
                showToast('Cannot save: team workspace not linked.', 'error');
                return;
            }
            if (!targetWorkspaceId) return;
            const trimmed = name.trim();
            if (!trimmed) return;
            const current = await readCurrentCompanies(targetWorkspaceId, vendorType);
            if (current.some(c => c.name.toLowerCase() === trimmed.toLowerCase())) {
                throw new Error(`"${trimmed}" is already in the list.`);
            }
            const entry = { id: generateCompanyId(), name: trimmed };
            const updated = [...current, entry].sort((a, b) => a.name.localeCompare(b.name));
            await setDoc(companyListDocRef(targetWorkspaceId, vendorType), { companies: updated });
            showToast('Company added.');
        }),
    [targetWorkspaceId, tawMissingParent, run, showToast]);

    const updateCompany = React.useCallback((vendorType, companyId, newName) =>
        run(async () => {
            if (tawMissingParent) {
                showToast('Cannot save: team workspace not linked.', 'error');
                return;
            }
            if (!targetWorkspaceId) return;
            const trimmed = newName.trim();
            if (!trimmed) return;
            const current = await readCurrentCompanies(targetWorkspaceId, vendorType);
            if (current.some(c => c.id !== companyId && c.name.toLowerCase() === trimmed.toLowerCase())) {
                throw new Error(`"${trimmed}" is already in the list.`);
            }
            const updated = current
                .map(c => c.id === companyId ? { ...c, name: trimmed } : c)
                .sort((a, b) => a.name.localeCompare(b.name));
            await setDoc(companyListDocRef(targetWorkspaceId, vendorType), { companies: updated });
            showToast('Company updated.');
        }),
    [targetWorkspaceId, tawMissingParent, run, showToast]);

    const deleteCompany = React.useCallback((vendorType, companyId) =>
        run(async () => {
            if (tawMissingParent) {
                showToast('Cannot save: team workspace not linked.', 'error');
                return;
            }
            if (!targetWorkspaceId) return;
            const current = await readCurrentCompanies(targetWorkspaceId, vendorType);
            const updated = current.filter(c => c.id !== companyId);
            await setDoc(companyListDocRef(targetWorkspaceId, vendorType), { companies: updated });
            showToast('Company removed.');
        }),
    [targetWorkspaceId, tawMissingParent, run, showToast]);

    const migrateCompanies = React.useCallback(async (vendorType, vendors) => {
        if (tawMissingParent) return;
        if (!targetWorkspaceId || !Array.isArray(vendors)) return;
        try {
            const ref = companyListDocRef(targetWorkspaceId, vendorType);
            const snap = await getDoc(ref);
            if (snap.exists()) return;

            const seen = new Set();
            const companies = [];
            vendors.forEach(vendor => {
                const name = (vendor.company || '').trim();
                if (name && !seen.has(name.toLowerCase())) {
                    seen.add(name.toLowerCase());
                    companies.push({ id: generateCompanyId(), name });
                }
            });

            await setDoc(ref, { companies });
        } catch (err) {
            console.warn(`Company migration for ${vendorType} failed:`, err);
        }
    }, [targetWorkspaceId, tawMissingParent]);

    return { addCompany, updateCompany, deleteCompany, migrateCompanies };
}
